import { MoreHorizontal } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "@/components/ui/overlays";
import { useT } from "@/lib/i18n";

export type MoreItem = {
  /** Stable key (defaults to the index). */
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  shortcut?: ReactNode;
  disabled?: boolean;
  /** Destructive: always rendered last, after a separator, in the danger colour. */
  danger?: boolean;
  /** Optional section heading; consecutive items with the same section are grouped. */
  section?: string;
  testId?: string;
};

/**
 * The one overflow menu ("More" / "المزيد") for secondary and rare actions.
 * Sections keep their order; danger items always go last, separated.
 */
export function MoreMenu({ items, label, align = "end", size = "sm", variant = "outline", iconOnly, testId }: { items: MoreItem[]; label?: string; align?: "start" | "end"; size?: "xs" | "sm" | "md"; variant?: "outline" | "ghost" | "secondary"; iconOnly?: boolean; testId?: string }) {
  const { t } = useT();
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  const text = label ?? t("kit.more");
  const safe = visible.filter((i) => !i.danger);
  const danger = visible.filter((i) => i.danger);
  // group consecutive items by section
  const groups: { section?: string; items: MoreItem[] }[] = [];
  for (const it of safe) {
    const last = groups[groups.length - 1];
    if (last && last.section === it.section) last.items.push(it);
    else groups.push({ section: it.section, items: [it] });
  }
  if (danger.length) groups.push({ items: danger });
  return (
    <Menu>
      <MenuTrigger asChild>
        {iconOnly ? (
          <Button variant={variant === "outline" ? "ghost" : variant} size={size === "md" ? "icon" : size === "xs" ? "icon-xs" : "icon-sm"} aria-label={text} title={text} data-testid={testId}>
            <MoreHorizontal />
          </Button>
        ) : (
          <Button variant={variant} size={size} icon={<MoreHorizontal />} data-testid={testId}>
            {text}
          </Button>
        )}
      </MenuTrigger>
      <MenuContent align={align}>
        {groups.map((g, gi) => (
          <Fragment key={gi}>
            {gi > 0 && <MenuSeparator />}
            {g.section && <MenuLabel>{g.section}</MenuLabel>}
            {g.items.map((it, i) => (
              <MenuItem key={it.id ?? `${gi}-${i}`} icon={it.icon} shortcut={it.shortcut} disabled={it.disabled} danger={it.danger} onSelect={it.onSelect} data-testid={it.testId}>
                {it.label}
              </MenuItem>
            ))}
          </Fragment>
        ))}
      </MenuContent>
    </Menu>
  );
}
